/**
 * CancelOrderModal — Phase 43.11 / 43.60
 *
 * Modale di conferma annullamento ordine cliente. Rendering dinamico in base
 * a `funding_source` ritornato dal backend (cancel-preview):
 *
 *   - unpaid         → "Nessun pagamento effettuato. Annulla senza costi."
 *   - stripe_single  → riepilogo CHF (importo pagato, penale, rimborso)
 *   - subscription   → "1 corsa verrà riaccreditata sul tuo piano"
 *   - delivery_pack  → "1 corsa bonus verrà riaccreditata sul tuo Credito Consegne"
 *
 * Mai mostrare CHF/rimborso se ordine NON pagato o coperto da credito.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, ShieldAlert, Repeat, Package, Wallet, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatCHF } from "@/lib/format";
import MobileModal from "@/components/MobileModal";
import { cancellationSuccessMessage } from "@/lib/orderRefund";

const FUNDING_META = {
  unpaid: {
    Icon: CheckCircle2, accent: "emerald",
    headline: "Annulla senza costi",
    sub: "Nessun pagamento è stato effettuato per questo ordine.",
  },
  subscription: {
    Icon: Repeat, accent: "violet",
    headline: "1 corsa verrà riaccreditata sul piano",
    sub: "Nessun importo monetario coinvolto: la corsa torna disponibile sul tuo abbonamento.",
  },
  delivery_pack: {
    Icon: Package, accent: "emerald",
    headline: "1 corsa bonus verrà riaccreditata",
    sub: "La corsa utilizzata torna disponibile sul tuo Credito Consegne (Pacchetto extra).",
  },
  wallet: {
    Icon: Wallet, accent: "emerald", headline: "Credito wallet",
    sub: "Il credito utilizzato viene gestito separatamente dal pagamento monetario.",
  },
  wallet_and_stripe: {
    Icon: Wallet, accent: "amber", headline: "Credito wallet e pagamento",
    sub: "Il credito e la quota monetaria hanno riaccrediti separati.",
  },
  stripe_single: {
    Icon: Wallet, accent: "amber",
    headline: "Rimborso CHF",
    sub: "Riepilogo importo pagato, eventuale penale e rimborso.",
  },
};

export default function CancelOrderModal({ orderId, onClose, onCancelled }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/orders/${orderId}/cancel-preview`);
        if (!cancelled) setPreview(data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orderId]);

  const submit = async () => {
    if (preview?.reason_required && !reason.trim()) {
      toast.error("Inserisci un motivo per l'annullamento.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/orders/${orderId}/customer-cancel`, { reason: reason.trim() || null });
      toast.success(cancellationSuccessMessage(data));
      onCancelled?.(data);
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const funding = preview?.funding_source || "stripe_single";
  const meta = preview?.manual_review_required
    ? { Icon: ShieldAlert, accent: "amber", headline: "Riaccredito da verificare", sub: preview.message }
    : FUNDING_META[funding] || FUNDING_META.stripe_single;
  const FundingIcon = meta.Icon;
  const isMonetary = funding === "stripe_single";

  // Phase 44.17 — Footer sticky con CTA: Annulla + Conferma annullamento.
  // Sempre visibili anche con body lungo (penali, breakdown, textarea).
  const footer = (!loading && preview && preview.allowed) ? (
    <div className="flex items-center gap-2">
      <button
        onClick={onClose}
        disabled={submitting}
        data-testid="cancel-modal-back"
        className="btn-ghost flex-1"
      >
        Indietro
      </button>
      <button
        onClick={submit}
        disabled={submitting}
        data-testid="cancel-modal-confirm"
        className="flex-1 rounded-full bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold py-2.5 disabled:opacity-60"
      >
        {submitting ? "Annullo…" : (funding === "unpaid" ? "Annulla ordine" : "Conferma annullamento")}
      </button>
    </div>
  ) : null;

  return (
    <MobileModal
      open
      onClose={onClose}
      title="Vuoi davvero annullare?"
      description="Annulla ordine"
      testid="cancel-order-modal"
      size="md"
      footer={footer}
    >
      <div className="space-y-4">
        {loading && (
          <div className="py-8 text-center text-dapnow-muted text-sm flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Verifica in corso…
          </div>
        )}

        {!loading && preview && !preview.allowed && (
          <div className="rounded-2xl bg-rose-50 border border-rose-200 p-4 space-y-2" data-testid="cancel-blocked">
            <div className="flex items-start gap-2">
              <ShieldAlert className="h-4 w-4 text-rose-700 mt-0.5" />
              <p className="text-sm text-rose-900 font-semibold">Annullamento non disponibile</p>
            </div>
            <p className="text-xs text-rose-900 leading-snug">{preview.message}</p>
            <a
              href="mailto:support@dap-now.ch?subject=Richiesta%20supporto%20ordine"
              data-testid="cancel-contact-support"
              className="inline-flex items-center gap-1 rounded-full bg-rose-700 hover:bg-rose-800 text-white text-xs font-bold px-3 py-1.5 mt-1"
            >
              Contatta supporto
            </a>
          </div>
        )}

        {!loading && preview && preview.allowed && (
          <>
            <FundingBanner meta={meta} FundingIcon={FundingIcon} funding={funding} />

            {(funding === "stripe_single" || funding.startsWith("wallet") || preview.penalty_chf > 0) && (
              <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2" data-testid="cancel-policy-message">
                <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5" />
                <p className="text-xs text-amber-900 leading-snug">{preview.message}</p>
              </div>
            )}

            {isMonetary && (
              <div className="rounded-2xl bg-dapnow-bg p-4 space-y-1.5" data-testid="cancel-breakdown">
                <p className="overline">Riepilogo</p>
                <Row label="Importo pagato" value={formatCHF(preview.gross_price)} />
                {preview.penalty_chf > 0 && (
                  <Row
                    label={`Penale (${preview.penalty_pct}%)`}
                    value={`− ${formatCHF(preview.penalty_chf)}`}
                    tone="amber"
                    testid="cancel-penalty"
                  />
                )}
                <Row
                  label="Rimborso a te"
                  value={formatCHF(preview.refund_chf)}
                  tone="emerald"
                  strong
                  testid="cancel-refund"
                />
              </div>
            )}

            {["subscription", "delivery_pack"].includes(funding) && !preview.manual_review_required && (
              <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 space-y-1.5" data-testid="cancel-credit-restore">
                <p className="overline text-emerald-700">Credito che verrà ripristinato</p>
                <Row
                  label={funding === "subscription" ? "Corsa abbonamento" : "Corsa bonus (Credito Consegne)"}
                  value="+1 corsa"
                  tone="emerald"
                  strong
                />
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-xs font-semibold tracking-tight">
                Motivo annullamento
                {preview.reason_required && <span className="text-rose-600"> *</span>}
                {!preview.reason_required && <span className="text-dapnow-muted font-normal"> (opzionale)</span>}
              </label>
              <textarea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="cancel-reason"
                placeholder="Es. non più necessario, errore nell'indirizzo, …"
                className="w-full max-w-full rounded-xl border border-dapnow-line px-3 py-2 text-sm focus:border-dapnow-blue/40 focus:outline-none"
              />
            </div>
          </>
        )}
      </div>
    </MobileModal>
  );
}

function FundingBanner({ meta, FundingIcon, funding }) {
  const palette = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    violet: "bg-violet-50 border-violet-200 text-violet-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
  }[meta.accent] || "bg-dapnow-bg border-dapnow-line text-dapnow-ink";
  return (
    <div
      className={`rounded-2xl border p-3 flex items-start gap-2.5 ${palette}`}
      data-testid={`cancel-funding-${funding}`}
    >
      <FundingIcon className="h-4 w-4 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-sm font-bold leading-tight">{meta.headline}</p>
        <p className="text-xs opacity-90 mt-0.5 leading-snug">{meta.sub}</p>
      </div>
    </div>
  );
}

function Row({ label, value, tone, strong, testid }) {
  const v = tone === "emerald" ? "text-emerald-700"
        : tone === "amber" ? "text-amber-700" : "text-dapnow-ink";
  return (
    <div className="flex items-center justify-between text-sm" data-testid={testid}>
      <span className="text-dapnow-muted">{label}</span>
      <span className={`${strong ? "font-bold" : "font-semibold"} ${v}`}>{value}</span>
    </div>
  );
}
